import React, { useEffect } from "react";
import { CreditCard, ShieldCheck } from "lucide-react";
import { useApi } from "../../hooks/useApi";
import { cn } from "../../lib/utils";
import { isOnlinePayableMethod } from "../../lib/onlinePayment";

export default function PaymentSection({
  payments,
  setPayments,
  paymentMethod,
  setPaymentMethod,
  paymentMethodSelected,
  setPaymentMethodSelected,
  onlinePayment = null, // { available, providerName } de useOnlinePayment
  payOnline = false,
  setPayOnline = () => {},
}) {
  const api = useApi();

  // Carrega métodos de pagamento
  useEffect(() => {
    const fetchPayments = async () => {
      try {
        const data = await api.get("/payments");
        setPayments(data.data || []);
      } catch (error) {
        console.error("Erro ao carregar métodos de pagamento:", error);
      }
    };

    if (payments.length === 0) {
      fetchPayments();
    }
  }, [api, payments.length, setPayments]);

  // "Pagar online agora" só para Pix/cartão e quando a loja tem integração ativa
  const canPayOnline =
    !!onlinePayment?.available &&
    !!paymentMethod &&
    isOnlinePayableMethod(paymentMethod, paymentMethodSelected);

  return (
    <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6">
      <h2 className="text-lg sm:text-xl font-bold text-gray-900 flex items-center gap-2 mb-4">
        <CreditCard className="w-5 h-5 sm:w-6 sm:h-6 text-brand-primary" />
        Forma de Pagamento
      </h2>

      <div className="space-y-2">
        {payments.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary mx-auto mb-2"></div>
            <p className="text-sm">Carregando formas de pagamento...</p>
          </div>
        ) : (
          payments.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => {
                setPaymentMethod(p.id);
                setPaymentMethodSelected(p.name || p.description || "");
              }}
              className={`w-full text-left px-4 py-3 rounded-lg border-2 transition flex items-center justify-between ${
                paymentMethod === p.id
                  ? "border-brand-primary bg-red-50 text-brand-primary"
                  : "border-gray-200 hover:border-gray-300"
              }`}
            >
              <span className="font-medium">{p.name || p.description}</span>
              {paymentMethod === p.id && (
                <span className="text-xs font-semibold">Selecionado</span>
              )}
            </button>
          ))
        )}
      </div>

      {canPayOnline && (
        <div className="mt-4 rounded-2xl border border-brand-600/30 bg-cream p-3 sm:p-4">
          <p className="text-sm font-semibold text-cocoa mb-2">
            Quando você quer pagar?
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setPayOnline(true)}
              className={cn(
                "min-h-[44px] text-left px-4 py-3 rounded-xl border-2 transition",
                payOnline
                  ? "border-brand-600 bg-white text-cocoa"
                  : "border-transparent bg-white/60 text-cocoa-muted hover:border-brand-600/40"
              )}
            >
              <span className="flex items-center gap-2 font-semibold">
                <ShieldCheck className="w-4 h-4 text-success" />
                Pagar online agora
              </span>
              <span className="block text-xs mt-1 text-cocoa-muted">
                Pagamento seguro
                {onlinePayment.providerName
                  ? ` via ${onlinePayment.providerName}`
                  : ""}
                . Você volta para cá ao concluir.
              </span>
            </button>
            <button
              type="button"
              onClick={() => setPayOnline(false)}
              className={cn(
                "min-h-[44px] text-left px-4 py-3 rounded-xl border-2 transition",
                !payOnline
                  ? "border-brand-600 bg-white text-cocoa"
                  : "border-transparent bg-white/60 text-cocoa-muted hover:border-brand-600/40"
              )}
            >
              <span className="font-semibold">Pagar depois</span>
              <span className="block text-xs mt-1 text-cocoa-muted">
                Na entrega/retirada ou combinando com a loja.
              </span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
